import { useState, useEffect, useCallback } from 'react';

/**
 * Hook för att spara tillstånd i sessionStorage, vilket gör att appens
 * tillstånd bevaras även vid siduppdateringar och liknande.
 * 
 * Förbättrad med felhantering och verifiering av sparade värden.
 * 
 * @param key Nyckeln under vilken tillståndet sparas i sessionStorage
 * @param initialValue Startvärdet om inget finns sparat
 * @returns [värde, funktion för att uppdatera värdet, funktion för att återställa värdet]
 */
function useSessionState<T>(
  key: string, 
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void, () => void] {
  // Användarsäker hämtning av värde från sessionStorage
  const getStoredValue = useCallback((): T => {
    try {
      // Hämta från sessionStorage
      const item = window.sessionStorage.getItem(key);
      
      // Om inget finns lagrat, returnera initialValue
      if (!item) return initialValue;
      
      // Tolka det lagrade värdet
      try {
        const parsedItem = JSON.parse(item);
        
        // Verifiera att det parsade värdet är giltigt
        // Om det är null eller undefined, använd initialValue istället
        if (parsedItem === null || parsedItem === undefined) {
          console.warn(`Värdet för "${key}" var null eller undefined, använder initialValue istället.`);
          return initialValue;
        }
        
        return parsedItem;
      } catch (parseError) {
        console.error(`Fel vid tolkning av värdet för "${key}":`, parseError);
        
        // Om tolkning misslyckas, ta bort det felaktiga värdet
        window.sessionStorage.removeItem(key);
        return initialValue;
      }
    } catch (error) {
      // Om det uppstår ett fel, logga det och returnera initialValue
      console.error(`Fel vid läsning av sessionStorage-nyckeln "${key}":`, error);
      return initialValue;
    }
  }, [key, initialValue]);

  // Använd state med getStoredValue som initial värdehämtare
  const [storedValue, setStoredValue] = useState<T>(getStoredValue);

  // Funktion för att uppdatera värdet i både state och sessionStorage
  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      // Spara värdet till state
      setStoredValue(prev => {
        // Hantera funktionsuppdateringar
        const valueToStore = value instanceof Function ? value(prev) : value;
        
        try {
          // Spara till sessionStorage
          if (valueToStore === undefined) {
            console.warn(`Försökte spara undefined för "${key}", sparar null istället.`);
            window.sessionStorage.setItem(key, JSON.stringify(null));
          } else {
            window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
          }
        } catch (error) {
          console.error(`Fel vid inställning av sessionStorage-nyckeln "${key}":`, error);
        }
        
        return valueToStore;
      });
    } catch (error) {
      console.error(`Oväntad fel vid uppdatering av värdet för "${key}":`, error);
    }
  }, [key]);

  // Funktion för att återställa värdet till initialValue
  const resetValue = useCallback(() => {
    try {
      // Ta bort från sessionStorage
      window.sessionStorage.removeItem(key);
      
      // Återställ state till initialValue
      setStoredValue(initialValue);
      
      console.log(`Värdet för "${key}" har återställts till initialValue.`);
    } catch (error) {
      console.error(`Fel vid återställning av värdet för "${key}":`, error);
    }
  }, [key, initialValue]);

  // Synkronisera värdet om det ändras i en annan flik
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.storageArea === window.sessionStorage) {
        try {
          if (e.newValue === null) {
            // Värdet har tagits bort, återställ till initialValue
            setStoredValue(initialValue);
          } else {
            // Tolka och uppdatera värdet
            const newValue = JSON.parse(e.newValue);
            setStoredValue(newValue);
          }
        } catch (error) {
          console.error(`Fel vid hantering av storage-event för "${key}":`, error);
        }
      }
    };

    // Lägg till event-lyssnare
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      // Ta bort event-lyssnare vid cleanup
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key, initialValue]);

  return [storedValue, setValue, resetValue];
}

export default useSessionState;
